import { Theme } from '@mui/material';
import type { PickerComponents } from '@mui/x-date-pickers/themeAugmentation';
import CalendarIcon from '@mui/icons-material/CalendarToday';
import RightIcon from '@mui/icons-material/KeyboardArrowRight';
import LeftIcon from '@mui/icons-material/KeyboardArrowLeft';

const commonPickerComponentProps = {
  components: {
    OpenPickerIcon: CalendarIcon,
    LeftArrowIcon: LeftIcon,
    RightArrowIcon: RightIcon,
  },
};

const buildMuiPickersDayComponent = (): PickerComponents<Omit<Theme, 'components'>>['MuiPickersDay'] => ({
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: '4px',
      '&:focus': {
        backgroundColor: theme.palette.primary[20],
      },
      '&:hover': {
        backgroundColor: theme.palette.primary[10],
      },
      '&:focus.Mui-selected, &.Mui-selected': {
        backgroundColor: theme.palette.secondary.main,
      },
    }),
    today: ({ theme }) => ({
      '&&': { borderColor: theme.palette.secondary.main },
    }),
  },
});

const buildMuiPickersYearComponent = (): PickerComponents<Omit<Theme, 'components'>>['MuiPickersYear'] => ({
  styleOverrides: {
    root: ({ theme }) => ({
      '& > .MuiPickersYear-yearButton': {
        borderRadius: '4px',
        '&.Mui-selected': {
          color: theme.palette.common.white,
          backgroundColor: `${theme.palette.secondary.main}!important`,
        },
        '&:focus': {
          backgroundColor: theme.palette.primary[20],
        },
        '&:hover': {
          backgroundColor: theme.palette.primary[10],
        },
      },
    }),
  },
});

const buildMuiClockComponent = (): PickerComponents<Omit<Theme, 'components'>>['MuiClock'] => ({
  styleOverrides: {
    pin: ({ theme }) => ({
      backgroundColor: theme.palette.secondary.main,
    }),
  },
});

const buildMuiClockPointerComponent = (): PickerComponents<Omit<Theme, 'components'>>['MuiClockPointer'] => ({
  styleOverrides: {
    root: ({ theme }) => ({
      backgroundColor: theme.palette.secondary.main,
    }),
    thumb: ({ theme }) => ({
      borderColor: theme.palette.secondary.main,
    }),
  },
});

const buildMuiClockNumberComponent = (): PickerComponents<Omit<Theme, 'components'>>['MuiClockNumber'] => ({
  styleOverrides: {
    root: ({ theme }) => ({
      '&.Mui-selected': { backgroundColor: theme.palette.secondary.main },
    }),
  },
});

const buildMuiDateTimePickerComponent = (): PickerComponents<Omit<Theme, 'components'>>['MuiDateTimePicker'] => ({
  defaultProps: {
    ampm: false,
    format: 'dd/MM/yyyy HH:mm',
    ...commonPickerComponentProps,
  },
});

const buildMuiDatePickerComponent = (): PickerComponents<Omit<Theme, 'components'>>['MuiDatePicker'] => ({
  defaultProps: {
    format: 'dd/MM/yyyy',
    ...commonPickerComponentProps,
  },
});

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const buildMuiTimePickerComponent = (): PickerComponents<Omit<Theme, 'components'>>['MuiTimePicker'] => ({
  defaultProps: {
    ampm: false,
    format: 'HH:mm',
    ...commonPickerComponentProps,
  },
});

export const buildPickersComponents = () => ({
  MuiPickersDay: buildMuiPickersDayComponent(),
  MuiClock: buildMuiClockComponent(),
  MuiClockPointer: buildMuiClockPointerComponent(),
  MuiClockNumber: buildMuiClockNumberComponent(),
  MuiDateTimePicker: buildMuiDateTimePickerComponent(),
  MuiDatePicker: buildMuiDatePickerComponent(),
  MuiTimePicker: buildMuiTimePickerComponent(),
  MuiPickersYear: buildMuiPickersYearComponent(),
});
